<template>
    <v-row class="ma-0 pa-0" align="center" justify="center">
      <v-col cols="12" class="chart_style">
        <div class="metric_style_key">
          <span class="metric_style_key__item">
            <span class="metric_style_key__line metric_style_key__line--solid"></span>
            Reach
          </span>
          <span class="metric_style_key__item">
            <span class="metric_style_key__line metric_style_key__line--dashed"></span>
            Frequency
          </span>
        </div>
        <v-chart :id="chart_id" :ref="chart_ref" class="chart" :option="option" autoresize></v-chart>
      </v-col>
    </v-row>
  </template>
  
  <script>
  import VChart, { THEME_KEY } from "vue-echarts";
  import "echarts";
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
  
  use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);

  const COLORS = [
    "#70E000", "#A4133C", "#2AB7CA", "#3A0CA3", "#F6BD60", "#1F4E5F", "#F28482",
    "#2F5D50", "#9FE0D0", "#A44A3F", "#CDB4DB", "#D7263D", "#75B9E3", "#6B8E23",
    "#E8B4B8", "#1E3A8A", "#F77F00", "#6D597A", "#A7C957", "#D0006F", "#B7D3E9",
    "#4A6F46", "#F4A261", "#2E5AAC", "#9CAF88", "#7B2CBF", "#C19A6B", "#00B4D8",
    "#C08497", "#2A9D8F", "#E9C46A", "#3A86C8", "#B46A55", "#4EC5C1", "#D4A017",
    "#A2678A", "#D8C3A5", "#C06542", "#B8A1D9", "#D6E6F2"
  ];

  export default {
    name: "ReachChartV3",
    props: ["chart_title", "chart_data", "chart_id", "chart_ref", "chart_options"],
    components: {
      VChart,
    },
    provide: {
      [THEME_KEY]: "light",
    },
    computed: {
      option: function() {
        const chartData = Array.isArray(this.chart_data) ? this.chart_data : [];
        const headers = chartData[0] || [];
        const reachIdx = headers.indexOf("Reach");
        const frequencyIdx = headers.indexOf("Frequency");
        const channels = [];

        for(let row of chartData){
          if(row[0] !== "Face ID" && !channels.includes(row[0])){
            channels.push(row[0])
          }
        }

        const datasetWithFilters = [];
        const seriesList = [];
        channels.forEach((channel, index) => {
          const datasetId = "dataset_" + channel;
          const channelColor = COLORS[index % COLORS.length];

          datasetWithFilters.push({
            id: datasetId,
            fromDatasetId: "dataset_raw",
            transform: {
                type: "filter",
                config: {
                  and: [
                      { dimension: "Face ID", "=": channel }
                  ]
                }
            }
          });

          seriesList.push(this.create_line_series({
            name: channel,
            datasetId: datasetId,
            metricColumn: "Reach",
            yAxisIndex: 0,
            color: channelColor,
            lineType: "solid",
          }));

          seriesList.push(this.create_line_series({
            name: channel,
            datasetId: datasetId,
            metricColumn: "Frequency",
            yAxisIndex: 1,
            color: channelColor,
            lineType: "dashed",
          }));
        });

        return {
          animationDuration: 1000,
          dataset: [
              {
                  id: "dataset_raw",
                  source: chartData, 
              },
              ...datasetWithFilters
          ],
          title: {
            text: this.chart_title || "Cumulative Reach and Frequency Analysis",
            fontFamily: "Poppins",
            left: "center",
          },
          legend: {
            data: channels,
            type: "scroll",
            left: "center",
            top: "5%",
            width: "90%",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: 12
            }
          },
          tooltip: {
              order: "valueAsc",
              trigger: "axis",
              formatter: (params) => {
                if (!params || !params.length) {
                  return "";
                }
                const title = params[0].axisValueLabel || params[0].axisValue;

                const body = params.map(item => {
                    const metric = this.get_series_metric(seriesList, item.seriesIndex);
                    const metricIdx = metric === "Frequency" ? frequencyIdx : reachIdx;
                    const value = this.get_data_value(item.value, metric, metricIdx);
                    return `${item.marker}${item.seriesName || ""} ${metric}: ${this.format_metric_value(value, metric)}`;
                }).join("<br/>");

                return `<strong>${title}</strong><br/>${body}`;
              }
          },
          xAxis: {
              type: "category",
              axisLabel: {
                  fontSize: 12,
                  fontFamily: "Poppins"
              },
              name: "Date",
              nameLocation: "middle",
              nameGap: 30,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: "bold"
              }
          },
          yAxis: [
            {
              position: "left",
              axisLabel: {
                  formatter: function (value) {
                      return (value * 100).toFixed(0) + "%";
                  },
                  fontSize: 12,
                  fontFamily: "Poppins"
              },
              name: "Cumulative Reach %",
              nameLocation: "middle",
              nameGap: 45,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: "bold"
              }
            },
            {
              position: "right",
              axisLabel: {
                  formatter: function (value) {
                      return Number(value).toFixed(1);
                  },
                  fontSize: 12,
                  fontFamily: "Poppins"
              },
              name: "Cumulative Frequency",
              nameLocation: "middle",
              nameGap: 45,
              nameTextStyle: {
                  fontSize: 14,
                  fontFamily: "Poppins",
                  fontWeight: "bold"
              }
            }
          ],
          grid: {
              left: "4%",
              right: "4%",
              top: "12%",
              bottom: "7%",
              containLabel: true
          },
          series: seriesList
        };
      }
    },
    methods: {
      create_line_series({ name, datasetId, metricColumn, yAxisIndex, color, lineType }) {
        return {
          type: "line",
          datasetId: datasetId,
          showSymbol: false,
          name: name,
          metricColumn: metricColumn,
          yAxisIndex: yAxisIndex,
          lineStyle: {
            color: color,
            type: lineType,
          },
          itemStyle: {
            color: color,
          },
          labelLayout: {
              moveOverlap: "shiftY"
          },
          emphasis: {
              focus: "series"
          },
          encode: {
              x: "Date",
              y: metricColumn,
              label: ["Face ID", metricColumn],
              itemName: "Date",
              tooltip: [metricColumn]
          }
        };
      },
      get_series_metric(seriesList, seriesIndex) {
        const series = seriesList[seriesIndex] || {};
        return series.metricColumn || "Reach";
      },
      get_data_value(dataValue, metricColumn, metricIndex) {
        if (Array.isArray(dataValue)) {
          return dataValue[metricIndex];
        }
        if (dataValue && Object.prototype.hasOwnProperty.call(dataValue, metricColumn)) {
          return dataValue[metricColumn];
        }
        return dataValue;
      },
      format_metric_value(value, metric) {
        const numericValue = Number(value);
        if (value == null || value === "" || Number.isNaN(numericValue)) {
          return "N/A";
        }
        if (metric === "Reach") {
          return (numericValue * 100).toFixed(1) + "%";
        }
        return numericValue.toFixed(2);
      },
    },
  };
  </script>
  
  <style scoped>
  .chart_title {
    font-family: "Poppins" !important;
    font-size: 10pt !important;
    line-height: 40px;
  
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chart_style {
    height: 60vh;
    font-family: "Poppins" !important;
    position: relative;
  }
  .metric_style_key {
    align-items: center;
    display: flex;
    gap: 14px;
    position: absolute;
    right: 5%;
    top: 48px;
    z-index: 1;
    color: #333333;
    font-family: "Poppins" !important;
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }
  .metric_style_key__item {
    align-items: center;
    display: inline-flex;
    gap: 6px;
    white-space: nowrap;
  }
  .metric_style_key__line {
    border-top: 2px solid #555555;
    display: inline-block;
    width: 26px;
  }
  .metric_style_key__line--dashed {
    border-top-style: dashed;
  }
  </style>
  
